/**
 * Resolves relation custom field values (a stored record id) into display names.
 *
 * The cache and in-flight maps live at module scope so a name resolved for one
 * field/row is reused everywhere, and the same id rendered many times triggers
 * a single network request.
 */
const cache = new Map(); // `${entity}:${id}` -> { label, isFormer }
const inflight = new Map(); // `${entity}:${id}` -> Promise<{ label, isFormer }>

const EMPTY = { label: '—', isFormer: false };

const orgUser = {
    // The org "employees" endpoint returns login-capable platform Users scoped to the org.
    url: id => `/employees/${id}`,
    name: r => r?.name || `${r?.firstname || ''} ${r?.lastname || ''}`.trim(),
    // Someone who left the organization, or whose account was deactivated: the
    // endpoint still names them so the record stays readable, but the reader
    // should see that this is not a current member.
    isFormer: r => r?.is_former_member === true
};

const endpoints = {
    user: orgUser,
    employee: orgUser, // legacy alias: builtin fields (representative_id, assigned_to) are typed 'employee'
    // The HR/payroll employee record (EmployeeProfile); its display name comes from the linked user.
    employee_profile: {
        url: id => `/employee-profiles/${id}`,
        name: r => r?.name || '—'
    },
    contact: {
        url: id => `/contacts/${id}`,
        name: r => r?.display_name || `${r?.firstname || ''} ${r?.lastname || ''}`.trim()
    }
};

export function isRelationType(type) {
    return Object.prototype.hasOwnProperty.call(endpoints, type);
}

/**
 * @returns {Promise<{label: string, isFormer: boolean}>}
 */
export async function resolveRelation(entity, id, apiBase) {
    if (id === null || id === undefined || id === '') {
        return EMPTY;
    }

    const cfg = endpoints[entity];
    if (!cfg) {
        return EMPTY;
    }

    const key = `${entity}:${id}`;
    if (cache.has(key)) {
        return cache.get(key);
    }
    if (inflight.has(key)) {
        return inflight.get(key);
    }

    const promise = axios
        .get(`${apiBase}${cfg.url(id)}`)
        .then(res => {
            const label = cfg.name(res.data);
            return label ? { label, isFormer: cfg.isFormer?.(res.data) === true } : EMPTY;
        })
        .catch(() => EMPTY)
        .then(resolved => {
            cache.set(key, resolved);
            inflight.delete(key);
            return resolved;
        });

    inflight.set(key, promise);
    return promise;
}
